import {
  AMBIGUOUS_EMAIL_MESSAGE,
  lookupExactEmail,
  type EmailQuery,
} from '@/lib/identity/exact-email-lookup';
import type { SupabaseClient, User } from '@supabase/supabase-js';

/**
 * Highest-blast-radius query in the codebase — called from every server
 * action's role gate, requireModule, resolveUserContext. A PostgREST
 * 400 here (schema drift on users table, RLS mis-config, bad select
 * string) used to silently return null and collapse the whole platform
 * to "no tenant context" with zero observable signal. Now we log and
 * return null — we still don't throw, because the correct UX is the
 * caller's own "Not authenticated" path, but at least Sentry / console
 * captures the root cause.
 */
export async function lookupUserRecordByIdentity<T>(
  sb: SupabaseClient,
  user: Pick<User, 'id'>,
  select: string,
  signal?: AbortSignal,
): Promise<T | null> {
  const { record } = await lookupUserRecordByIdentityDetailed<T>(sb, user, select, signal);
  return record;
}

/**
 * Detailed variant that distinguishes "the query ran and found nothing"
 * (`failed: false, record: null` — a POSITIVE empty answer) from "the query
 * errored" (`failed: true` — an INDETERMINATE answer: transient PostgREST /
 * network / RLS failure that says nothing about whether the row exists).
 *
 * P1 2026-08-19: the plain wrapper collapses both cases to `null`, which let a
 * transient users-table read failure masquerade as "account not linked to an
 * organisation" (a permanent account-state claim) in the dashboard gate, the
 * signout route, and the login action. Callers that render permanent
 * account-state copy MUST use this variant and treat `failed: true` as
 * transient, never as a definitive missing account.
 */
export async function lookupUserRecordByIdentityDetailed<T>(
  sb: SupabaseClient,
  user: Pick<User, 'id'>,
  select: string,
  signal?: AbortSignal,
): Promise<{ record: T | null; failed: boolean }> {
  if (signal?.aborted) return { record: null, failed: true };
  if (!user.id) return { record: null, failed: false };

  let query = sb.from('users').select(select).eq('auth0_id', user.id);
  if (signal) query = query.abortSignal(signal);
  const { data, error } = await query.maybeSingle();
  if (signal?.aborted) return { record: null, failed: true };
  if (error) {
    // Don't swallow — surface the PostgREST error so schema drift /
    // RLS denial / typo'd select strings show up in logs instead of
    // being indistinguishable from a legitimately missing user.
    console.error(
      '[auth] lookupUserRecordByIdentity failed for auth_id=%s: %s',
      user.id,
      error.message,
    );
    return { record: null, failed: true };
  }
  return { record: (data as T | null) ?? null, failed: false };
}

export async function lookupTenantUserRecordByEmail<T>(
  sb: SupabaseClient,
  tenantId: string,
  email: string,
  select: string,
): Promise<T | null> {
  const normalizedEmail = email.trim();
  if (!normalizedEmail) return null;

  type Row = { email: string | null } & Record<string, unknown>;
  // The column list is a runtime string, so supabase-js cannot infer the row type.
  const { data, error, ambiguous } = await lookupExactEmail(
    email,
    () =>
      sb
        .from('users')
        .select(`${select}, email` as string)
        .eq('tenant_id', tenantId) as unknown as EmailQuery<Row>,
  );
  if (ambiguous) {
    console.error('[auth] lookupTenantUserRecordByEmail: %s', AMBIGUOUS_EMAIL_MESSAGE);
    return null;
  }
  if (error) {
    console.error(
      '[auth] lookupTenantUserRecordByEmail failed for tenant=%s email=%s: %s',
      tenantId,
      normalizedEmail,
      error.message,
    );
  }
  if (!data) return null;
  // email is selected only for verification when the caller did not request it.
  if (!/(?:^|,)\s*(?:email|\*)\s*(?:,|$)/.test(select)) {
    const result = { ...data };
    delete (result as Record<string, unknown>).email;
    return result as T;
  }
  return data as T;
}
